/** Payoff data for one positional basket (GET /api/trades/{id}/payoff).
 *
 * The open basket for a strategy is discovered through /api/trades —
 * phase D keeps open baskets visible regardless of the date range, so
 * the default (today) window is enough. Backend returns snake_case;
 * this module converts to the camelCase view-model. */

import { getJson } from "@/lib/http";

interface PayoffLegApi {
  side: "BUY" | "SELL";
  symbol: string;
  strike: number;
  option_type: string;
  quantity: number;
  entry_fill: number;
  ltp?: number | null;
}

interface PayoffApi {
  underlying: string | null;
  expiry: string | null;
  connected: boolean;
  spot: number | null;
  spots: number[];
  expiry_pnl: number[];
  today_pnl: number[] | null;
  breakevens: number[];
  max_profit: number;
  max_loss: number;
  max_profit_unbounded: boolean;
  max_loss_unbounded: boolean;
  net_entry_cost: number;
  last_mtm: number | null;
  legs: PayoffLegApi[];
}

interface OpenBasketApi {
  id: string;
  horizon?: string;
  exit_at: string | null;
}

export interface PayoffLegView {
  side: "BUY" | "SELL";
  symbol: string;
  strike: number;
  optionType: string;
  quantity: number;
  entryFill: number;
  /** Live premium; null while the broker session is down. */
  ltp: number | null;
}

export interface PayoffView {
  underlying: string | null;
  expiry: string | null;
  connected: boolean;
  spot: number | null;
  spots: number[];
  expiryPnl: number[];
  todayPnl: number[] | null;
  breakevens: number[];
  maxProfit: number;
  maxLoss: number;
  maxProfitUnbounded: boolean;
  maxLossUnbounded: boolean;
  netEntryCost: number;
  lastMtm: number | null;
  legs: PayoffLegView[];
}

export const payoffApi = {
  /** The strategy's open trade id (basket or intraday), or null when
   * flat. `executionMode` must match the strategy — /api/trades
   * segregates paper from live and defaults to LIVE. */
  async openTradeId(strategyId: string, executionMode: "LIVE" | "PAPER"): Promise<string | null> {
    const rows = await getJson<OpenBasketApi[]>(
      `/api/trades?strategy_id=${strategyId}&execution_mode=${executionMode}`,
    );
    const open = rows.find((r) => r.exit_at === null);
    return open?.id ?? null;
  },

  async payoff(tradeId: string): Promise<PayoffView> {
    const raw = await getJson<PayoffApi>(`/api/trades/${tradeId}/payoff`);
    return {
      underlying: raw.underlying,
      expiry: raw.expiry,
      connected: raw.connected,
      spot: raw.spot,
      spots: raw.spots,
      expiryPnl: raw.expiry_pnl,
      todayPnl: raw.today_pnl,
      breakevens: raw.breakevens,
      maxProfit: raw.max_profit,
      maxLoss: raw.max_loss,
      maxProfitUnbounded: raw.max_profit_unbounded,
      maxLossUnbounded: raw.max_loss_unbounded,
      netEntryCost: raw.net_entry_cost,
      lastMtm: raw.last_mtm,
      legs: raw.legs.map((leg) => ({
        side: leg.side,
        symbol: leg.symbol,
        strike: leg.strike,
        optionType: leg.option_type,
        quantity: leg.quantity,
        entryFill: leg.entry_fill,
        ltp: leg.ltp ?? null,
      })),
    };
  },
};
